/**
 * User Store
 *
 * Manages user profile, settings, and preferences state. Provides actions for
 * fetching and updating the user's profile (with stats like XP and level),
 * notification settings, and privacy settings. Also supports GDPR-related
 * operations: data export and account deletion.
 *
 * @module useUserStore
 *
 * @example
 * ```tsx
 * import { useUserStore, selectProfile, selectXpProgress } from '@/store/useUserStore'
 *
 * function ProfilePage() {
 *   const { fetchProfile, updateProfile } = useUserStore()
 *   const profile = useUserStore(selectProfile)
 *   const xpPercent = useUserStore(selectXpProgress)
 *
 *   useEffect(() => { fetchProfile() }, [])
 *
 *   return (
 *     <div>
 *       <Avatar src={profile?.avatarUrl} />
 *       <span>Level {profile?.level} - {xpPercent}% to next</span>
 *     </div>
 *   )
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type { UserProfileWithStats, NotificationSettings } from '../types'
import { userApi } from '../api'

/**
 * Simple privacy settings for UI
 */
interface SimplePrivacySettings {
  profileVisibility: 'public' | 'communities' | 'private'
  showActivityStatus: boolean
  allowFriendRequests: boolean
  showCompletedChallenges: boolean
  showSavedList: boolean
}

interface UserState {
  // Profile data
  profile: UserProfileWithStats | null
  notificationSettings: NotificationSettings | null
  privacySettings: SimplePrivacySettings | null

  // Loading states
  isLoadingProfile: boolean
  isLoadingSettings: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions
  fetchProfile: () => Promise<void>
  updateProfile: (data: Partial<UserProfileWithStats>) => Promise<boolean>
  fetchNotificationSettings: () => Promise<void>
  updateNotificationSettings: (settings: Partial<NotificationSettings>) => Promise<boolean>
  fetchPrivacySettings: () => Promise<void>
  updatePrivacySettings: (settings: Partial<SimplePrivacySettings>) => Promise<boolean>
  exportData: () => Promise<object | null>
  deleteAccount: () => Promise<boolean>
  clearError: () => void
  reset: () => void
}

const initialState = {
  profile: null,
  notificationSettings: null,
  privacySettings: null,
  isLoadingProfile: false,
  isLoadingSettings: false,
  isSaving: false,
  error: null,
}

export const useUserStore = createWithEqualityFn<UserState>()((set, get) => ({
  ...initialState,

  fetchProfile: async () => {
    set({ isLoadingProfile: true, error: null })

    const response = await userApi.getProfile()

    if (response.success && response.data) {
      set({ profile: response.data, isLoadingProfile: false })
    } else {
      set({
        error: response.error ?? 'Failed to load profile',
        isLoadingProfile: false,
      })
    }
  },

  updateProfile: async (data) => {
    set({ isSaving: true, error: null })

    const response = await userApi.updateProfile(data)

    if (response.success) {
      // Refetch profile to get updated data with stats
      await get().fetchProfile()
      set({ isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update profile',
        isSaving: false,
      })
      return false
    }
  },

  fetchNotificationSettings: async () => {
    set({ isLoadingSettings: true, error: null })

    const response = await userApi.getNotificationSettings()

    if (response.success) {
      set({ notificationSettings: response.data, isLoadingSettings: false })
    } else {
      set({
        error: response.error ?? 'Failed to load notification settings',
        isLoadingSettings: false,
      })
    }
  },

  updateNotificationSettings: async (settings) => {
    set({ isSaving: true, error: null })

    const response = await userApi.updateNotificationSettings(settings)

    if (response.success) {
      set({ notificationSettings: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update notification settings',
        isSaving: false,
      })
      return false
    }
  },

  fetchPrivacySettings: async () => {
    set({ isLoadingSettings: true, error: null })

    const response = await userApi.getPrivacySettings()

    if (response.success) {
      set({ privacySettings: response.data, isLoadingSettings: false })
    } else {
      set({
        error: response.error ?? 'Failed to load privacy settings',
        isLoadingSettings: false,
      })
    }
  },

  updatePrivacySettings: async (settings) => {
    set({ isSaving: true, error: null })

    const response = await userApi.updatePrivacySettings(settings)

    if (response.success) {
      set({ privacySettings: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update privacy settings',
        isSaving: false,
      })
      return false
    }
  },

  exportData: async () => {
    set({ isSaving: true, error: null })

    const response = await userApi.exportUserData()

    set({ isSaving: false })

    if (response.success) {
      return response.data
    } else {
      set({ error: response.error ?? 'Failed to export data' })
      return null
    }
  },

  deleteAccount: async () => {
    set({ isSaving: true, error: null })

    const response = await userApi.deleteAccount()

    if (response.success) {
      set({ ...initialState })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete account',
        isSaving: false,
      })
      return false
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select the full user profile with stats. */
export const selectProfile = (state: UserState) => state.profile

/** Select the user's current level number (defaults to 1). */
export const selectLevel = (state: UserState) => state.profile?.level ?? 1

/** Select the user's total XP (defaults to 0). */
export const selectXp = (state: UserState) => state.profile?.xp ?? 0

/** Select XP required to reach the next level. */
export const selectXpToNextLevel = (state: UserState) => state.profile?.xpToNextLevel ?? 100

/**
 * Select XP progress as a percentage (0-100) toward the next level.
 *
 * @example
 * ```ts
 * const percent = useUserStore(selectXpProgress)
 * // 75 (meaning 75% of the way to the next level)
 * ```
 */
export const selectXpProgress = (state: UserState) => {
  const xp = state.profile?.xp ?? 0
  const xpToNext = state.profile?.xpToNextLevel ?? 100
  return Math.round((xp / xpToNext) * 100)
}
